import Avatar from 'common/Avatar/Avatar'
import { mentionsConversionOptions } from 'common/Prose/utils/mentionsConversionOptions'
import { MentionSubject } from 'common/Prose/utils/smartLinks/types'
import { MentionsFormatBar } from 'components/MentionsFormatBar/MentionsFormatBar'
import { UserProfileSidebar } from 'components/UserProfileSidebar/UserProfileSidebar'
import { useUserCard } from 'hooks/queries/users/useUserCard'
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Provider } from 'react-redux'
import store from 'redux/store'
import { getInitials } from 'utils'
import { getUserProfilePath } from 'utils/urlUtils'
import { type userCardProps } from '../../Card'
import Styles from '../../Card.module.scss'
import { CardViewComponent } from '../CardViewComponent/CardViewComponent'
import { FrameViewComponent } from '../FrameViewComponent/FrameViewComponent'

export const UserCard = (props: userCardProps) => {
  const {
    nodeViewContext,
    isSelected,
    isCommentSelected,
    comments,
    tasks,
    currentWorkspaceId,
    showMentionFormatBar,
    setShowMentionFormatBar,
    isUserSidebarOpen,
    setIsUserSidebarOpen,
  } = props
  const { node, getPos, view } = nodeViewContext
  const { userId, title } = node.attrs
  const containerRef = useRef<HTMLDivElement>(null)
  const [mentionSubject, setMentionSubject] = useState<MentionSubject>({
    title: title,
    avatar: { emoji: <Avatar size={16} label={'U'} /> },
  })

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { data: userData, isLoading, isError } = useUserCard(parseInt(userId!))

  useEffect(() => {
    if (!userData) return
    const { display_name, avatar_url, company_role } = userData.user

    setMentionSubject({
      avatar: { label: getInitials(display_name), url: avatar_url },
      title: display_name,
      role: company_role as string,
    })
  }, [isLoading, userId, userData])

  const url = useMemo(() => {
    return getUserProfilePath({ displayName: mentionSubject?.title, id: Number(userId) })
  }, [userId, mentionSubject])

  const subtitle = useMemo(() => {
    if (isLoading) return 'Loading...'
    return mentionSubject?.role ?? ''
  }, [isLoading, mentionSubject])

  const handleVisitLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (node?.attrs.mentionType === 'User') {
      event.preventDefault()
      setIsUserSidebarOpen(!isUserSidebarOpen)
    }
  }

  return (
    <div className={Styles.cardWrapper} ref={containerRef}>
      {node.attrs.mode === 'card' ? (
        <CardViewComponent
          cardStyle={node.attrs.cardStyle}
          selected={isSelected}
          commentSelected={isCommentSelected}
          tasks={tasks}
          comments={comments}
          mentionSubject={mentionSubject}
          mentionType={node.attrs.mentionType}
          subtitle={subtitle}
          url={url}
          loading={isLoading}
          isError={isError}
        />
      ) : (
        <FrameViewComponent
          nodeViewContext={nodeViewContext}
          comments={comments}
          tasks={tasks}
          mentionSubject={mentionSubject}
          subtitle={subtitle}
          selected={isSelected}
          commentSelected={isCommentSelected}
          workspaceId={currentWorkspaceId}
          memberId={node.attrs.userId}
          showInlineMenu={setShowMentionFormatBar}
          loading={isLoading}
          isError={isError}
        />
      )}
      {showMentionFormatBar && view.editable && (
        <MentionsFormatBar
          view={view}
          close={() => setShowMentionFormatBar(false)}
          getPos={getPos}
          mentionSubject={mentionSubject}
          subtitle={subtitle}
          link={url}
          node={node}
          onLinkClick={handleVisitLinkClick}
          conversionOptions={mentionsConversionOptions(node, getPos(), view, mentionSubject.title ?? '')}
          parentRef={containerRef}
        />
      )}

      <Provider store={store}>
        <UserProfileSidebar
          open={isUserSidebarOpen}
          onClose={() => setIsUserSidebarOpen(false)}
          workspaceId={currentWorkspaceId}
          memberId={parseInt(node.attrs.userId)}
          sidebarClassName={Styles.sidebar}
        />
      </Provider>
    </div>
  )
}
